import React from 'react';
import { Tabs } from 'antd';
import style from './style.module.scss';

export type TabType = 'line' | 'card' | 'editable-card';
interface SingleTab {
    name: string;
    key: string;
    children?: React.ReactNode;
    /** Pushes this tab to the far right of the tab bar. Meant for the last tab. */
    alignEnd?: boolean;
    /** Shown left of the name. Decorative: screen readers hear the name only. */
    icon?: React.ReactNode;
}

interface BaseTabsProps {
    tabsArray: SingleTab[];
    tabType?: TabType;
    /**
     * `page-header` is a full-width white bar meant to sit right under the
     * PageTopBar, with the tab content padded below it. Defaults to the
     * centred, inset tabs used elsewhere.
     */
    variant?: 'default' | 'page-header';
    /** Shown between the tab bar and the active tab's content, on every tab. */
    belowTabBar?: React.ReactNode;
    /**
     * Shown beside the active tab's content, under the tab bar. It sits
     * outside the tabs, so it stays mounted, with its state, while they change.
     */
    aside?: React.ReactNode;
    /** Hides the aside without unmounting it, on a tab it has nothing for. */
    isAsideHidden?: boolean;
}

// One without the other leaves antd's Tabs controlled but with no way to
// move off the initial key, so both are required together or neither.
type ActiveKeyProps =
    | { activeKey?: undefined; onChange?: undefined }
    | { activeKey: string; onChange: (key: string) => void };

type TabsProps = BaseTabsProps & ActiveKeyProps;

export const TopTabs = ({
    tabsArray,
    tabType,
    variant = 'default',
    belowTabBar,
    aside,
    isAsideHidden = false,
    activeKey,
    onChange,
}: TabsProps) => {
    const items = tabsArray?.map((tab) => ({
        key: tab.key,
        label:
            tab.alignEnd || tab.icon ? (
                <span
                    className={`${style.tab_label} ${
                        tab.alignEnd ? style.tab_label_end : ''
                    }`}
                >
                    {tab.icon && (
                        <span className={style.tab_icon} aria-hidden="true">
                            {tab.icon}
                        </span>
                    )}
                    {tab.name}
                </span>
            ) : (
                tab.name
            ),
        children: tab.children,
    }));

    // A key naming no tab (e.g. a hand-edited URL) would otherwise leave antd
    // with no matching pane to show; fall back to the first tab instead.
    const resolvedActiveKey =
        activeKey === undefined
            ? undefined
            : tabsArray?.find((tab) => tab.key === activeKey)?.key ??
              tabsArray?.[0]?.key;

    return (
        <div
            className={`${style.toptabs_container} ${
                variant === 'page-header' ? style.page_header : ''
            } ${aside === undefined ? '' : style.with_aside}`}
        >
            <Tabs
                type={tabType}
                items={items}
                activeKey={resolvedActiveKey}
                onChange={onChange}
                renderTabBar={
                    belowTabBar === undefined
                        ? undefined
                        : (props, DefaultTabBar) => (
                              <>
                                  <DefaultTabBar {...props} />
                                  <div className={style.below_tab_bar}>
                                      {belowTabBar}
                                  </div>
                              </>
                          )
                }
            />
            {aside !== undefined && (
                <div className={style.aside} hidden={isAsideHidden}>
                    {aside}
                </div>
            )}
        </div>
    );
};
